import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import {LoadingStates} from '@Utils/loading.util';
import {isUserPAYG} from '@Libraries/user.library';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {
  closeAiActionLoadingModal,
  isCurrentModalSession,
  openPublishingLoadingModalAndGetSessionId,
  showPublishingReadyModal,
} from '@Components/ai-chat-window/lib/ai-action-loading-modal.library';
import {
  clearPublishHandoff,
  isPublishHandedOffToGrowl,
  isPublishInFlightForGrowlHandoff,
  markPublishSettled,
  markPublishStarted,
  showPublishDoneGrowl,
  showPublishErrorGrowl,
  showPublishPopupBlockedGrowl,
} from '@Components/ai-chat-window/lib/publish-growl.library';
import {getPublishDestinationUrl, getPublishPendingPageUrl} from '@Components/ai-chat-window/lib/publish-destination.library';
import type {AiChatAssetKind} from '@Components/ai-chat-window/lib/publish-flow.types';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';

type SetPublishingLoadingState = (loadingOption: PublishingOption | null, loadingState: LoadingStates) => void;

export interface PublishJob {
  option: PublishingOption;
  trackAnalytics: () => void;
  fetchPosterHashedId: () => Promise<string>;
  /** Set when the job publishes one page of a user's own design. */
  pageHashedId: string | null;
  setLoadingState: SetPublishingLoadingState;
}

interface RunPublishFlowParams extends PublishJob {
  assetKind: AiChatAssetKind;
  hashedId: string;
}

/** Both entry points consult this, so the email upsell can never gate one of them and not the other. */
const isBlockedByEmailCampaignUpsell = (option: PublishingOption): boolean => {
  return option === PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN && isUserPAYG();
};

/**
 * Below this width a publish waits in the chat behind the loading modal; at or above it the wait moves to a
 * new tab. The number coincides with the modal library's mobile threshold today, but this is a flow decision
 * rather than a layout one — retuning modal sizing must not silently move users between publish flows.
 */
const IN_CHAT_PUBLISH_MAX_WIDTH = 781;

/** Long enough to absorb a double-click, short enough that the row never looks stuck. */
const PENDING_TAB_ROW_LOCK_MS = 1500;

const shouldPublishInChat = (): boolean => {
  return window.innerWidth < IN_CHAT_PUBLISH_MAX_WIDTH;
};

/**
 * Desktop hands the whole wait to a new tab, so the chat stays usable and there is no modal to dismiss. The
 * open must stay synchronous — no `await` may precede it — or the browser drops the click's user activation
 * and blocks the tab.
 */
const openPublishPendingTab = ({option, assetKind, hashedId, setLoadingState}: RunPublishFlowParams): void => {
  const pendingPageUrl = getPublishPendingPageUrl(assetKind, option, hashedId);
  const tab = window.open(pendingPageUrl, '_blank');

  if (!tab) {
    showPublishPopupBlockedGrowl(option, pendingPageUrl);
    return;
  }

  lockPublishRowsBriefly(option, setLoadingState);
};

/**
 * The new tab owns the job from here, so the chat is never told when it finishes and cannot clear a loading
 * state on completion. A short lock is enough to swallow a double-click into one tab, and releases itself so
 * the row can never sit spinning for a job it knows nothing about.
 */
const lockPublishRowsBriefly = (option: PublishingOption, setLoadingState: SetPublishingLoadingState): void => {
  setLoadingState(option, LoadingStates.LOADING);

  setTimeout((): void => {
    releaseRowsIfNoRunNeedsThem(setLoadingState);
  }, PENDING_TAB_ROW_LOCK_MS);
};

/**
 * The row state is per image, not per run, so whoever finishes first must not clear a spinner a sibling run
 * is still relying on. Only the last run to leave puts the rows back.
 */
const releaseRowsIfNoRunNeedsThem = (setLoadingState: SetPublishingLoadingState): void => {
  if (isPublishInFlightForGrowlHandoff()) {
    return;
  }

  setLoadingState(null, LoadingStates.NOT_LOADED);
};

/**
 * Mobile keeps the wait in the chat: a loading modal, and a growl to hand off to if the user dismisses it.
 */
const publishViaLoadingModal = async ({option, fetchPosterHashedId, pageHashedId, setLoadingState}: PublishJob): Promise<void> => {
  setLoadingState(option, LoadingStates.LOADING);
  const sessionId = openPublishingLoadingModalAndGetSessionId(option);
  markPublishStarted(sessionId);

  try {
    const posterHashedId = await fetchPosterHashedId();
    const navigationUrl = getPublishDestinationUrl(option, posterHashedId, pageHashedId);
    setLoadingState(option, LoadingStates.LOADED);

    if (isCurrentModalSession(sessionId) && !isPublishHandedOffToGrowl(sessionId)) {
      showPublishingReadyModal(option, {url: navigationUrl, openInNewTab: true});
      return;
    }

    showPublishDoneGrowl(sessionId, option, navigationUrl);
  } catch (e: unknown) {
    console.error(e);

    if (!isPublishHandedOffToGrowl(sessionId) && isCurrentModalSession(sessionId)) {
      closeAiActionLoadingModal();
    }

    showPublishErrorGrowl(sessionId);
  } finally {
    markPublishSettled(sessionId);
    clearPublishHandoff(sessionId);
    releaseRowsIfNoRunNeedsThem(setLoadingState);
  }
};

/**
 * Publishes without opening a tab, for callers that are not running inside a user gesture — a stream control
 * event fires seconds after the last click, so a `window.open` from there is blocked on every desktop
 * browser. Keeping those in the chat is what the pending tab is an optimisation of, not a replacement for.
 */
export const runPublishInChat = async (job: PublishJob): Promise<void> => {
  job.trackAnalytics();

  if (isBlockedByEmailCampaignUpsell(job.option)) {
    openUpsellingDialogForEmailCampaigns(UpsellDialogEntryPoint.AI_CHAT_PUBLISH);
    return;
  }

  await publishViaLoadingModal(job);
};

/**
 * Shared publish pipeline for AI Chat images and animations, for callers running inside a user gesture.
 * Desktop opens the pending tab and returns immediately; mobile waits in the chat behind the loading modal.
 */
export const runPublishFlow = async (params: RunPublishFlowParams): Promise<void> => {
  if (shouldPublishInChat()) {
    await runPublishInChat(params);
    return;
  }

  params.trackAnalytics();

  if (isBlockedByEmailCampaignUpsell(params.option)) {
    openUpsellingDialogForEmailCampaigns(UpsellDialogEntryPoint.AI_CHAT_PUBLISH);
    return;
  }

  openPublishPendingTab(params);
};
